import { useState } from 'react';
import { useAuth } from './useAuth';
import { useFreemium } from './useFreemium';
import { createCheckoutUrl } from '../lib/lemonsqueezy';
import { supabase } from '../lib/supabase';

export type SubscriptionPlan = 'monthly' | 'yearly';

interface UseUpgradeReturn {
  upgradeToPro: (plan: SubscriptionPlan) => void;
  isLoading: boolean;
  canUpgrade: boolean;
}

/**
 * useUpgrade - Hook for handling Pro subscription upgrades via LemonSqueezy
 *
 * Features:
 * - Creates checkout session via Supabase function
 * - Redirects to LemonSqueezy hosted checkout
 * - Webhook updates subscription tier on payment success
 */
export function useUpgrade(): UseUpgradeReturn {
  const { user } = useAuth();
  const { tier } = useFreemium();
  const [isLoading, setIsLoading] = useState(false);

  const upgradeToPro = async (plan: SubscriptionPlan): Promise<void> => {
    if (!user?.email) {
      console.error('[useUpgrade] No user email');
      alert('Please sign in to upgrade.');
      return;
    }

    if (tier === 'pro') {
      console.log('[useUpgrade] User already Pro');
      alert('You already have an active Pro subscription.');
      return;
    }

    setIsLoading(true);
    console.log(`[useUpgrade] Creating ${plan} checkout for ${user.email}...`);

    try {
      // Get auth session token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      console.log('[useUpgrade] Session check:', {
        hasSession: !!session,
        hasAccessToken: !!session?.access_token,
        error: sessionError
      });

      if (sessionError) {
        throw new Error(`Session error: ${sessionError.message}`);
      }

      if (!session?.access_token) {
        throw new Error('No auth session. Please sign in again.');
      }

      // Create checkout URL via our library (which calls Supabase function)
      const checkoutUrl = await createCheckoutUrl(
        plan,
        user.email,
        user.id,
        session.access_token
      );

      console.log('[useUpgrade] ✅ Checkout URL created, redirecting...');

      // Redirect to LemonSqueezy checkout
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('[useUpgrade] Failed to create checkout:', error);
      alert('Failed to start checkout. Please try again.');
      setIsLoading(false);
    }
  };

  return {
    upgradeToPro,
    isLoading,
    canUpgrade: tier !== 'pro'
  };
}
